import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { GeoFilters } from '@/components/GeoFilters';
import { MapContainer, TileLayer, CircleMarker, Popup, useMapEvents, useMap } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Loader2, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import 'leaflet/dist/leaflet.css';

const formatINR = (v) => {
  if (v >= 10000000) return `${(v / 10000000).toFixed(1)}Cr`;
  if (v >= 100000) return `${(v / 100000).toFixed(1)}L`;
  if (v >= 1000) return `${(v / 1000).toFixed(1)}K`;
  return v?.toLocaleString('en-IN') || '0';
};

const getOutletColor = (item) => {
  if (!item.total_orders && item.total_orders !== 0) {
    // City-level marker
    if (item.active && item.count) {
      const ratio = item.active / item.count;
      if (ratio > 0.8) return '#10B981';
      if (ratio > 0.5) return '#F59E0B';
      return '#EF4444';
    }
    return '#3B82F6';
  }
  // Outlet-level marker
  if (item.total_orders === 0) return '#9CA3AF'; // grey - no data
  if (item.trend_pct <= -25) return '#EF4444';  // red - declining >25%
  if (item.trend_pct < 0) return '#F59E0B';     // amber - declining
  return '#10B981';                               // green - stable/growing
};

// Component to detect zoom and bounds changes
function MapEventHandler({ onBoundsChange, onZoomChange }) {
  const map = useMapEvents({
    moveend: () => {
      const bounds = map.getBounds();
      const zoom = map.getZoom();
      onZoomChange(zoom);
      if (zoom >= 12) {
        onBoundsChange({
          south: bounds.getSouth(),
          west: bounds.getWest(),
          north: bounds.getNorth(),
          east: bounds.getEast(),
        });
      }
    },
    zoomend: () => {
      onZoomChange(map.getZoom());
    },
  });
  return null;
}

export default function MapPage() {
  const { api } = useAuth();
  const [cityData, setCityData] = useState([]);
  const [outletData, setOutletData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [outletLoading, setOutletLoading] = useState(false);
  const [zones, setZones] = useState([]);
  const [zone, setZone] = useState('');
  const [subzone, setSubzone] = useState('');
  const [state, setState] = useState('');
  const [city, setCity] = useState('');
  const [zoom, setZoom] = useState(5);
  const [meta, setMeta] = useState(null);

  // Map-specific zone list: spellings merged, with how many outlets can be plotted
  useEffect(() => {
    api.get('/map/zones').then(r => setZones(r.data || [])).catch(() => {});
  }, [api]);

  // Load city-level data initially
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const params = {};
        if (zone) params.zone = zone;
        if (subzone) params.subzone = subzone;
        if (state) params.state = state;
        if (city) params.city = city;
        const res = await api.get('/map/outlets', { params });
        if (res.data.type === 'cities') {
          setCityData(res.data.data || []);
        }
        setMeta(res.data.meta || null);
      } catch (err) {
        toast.error('Failed to load map data');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [api, zone, subzone, state, city]);

  // Load outlet-level data when bounds change (zoomed in)
  const loadOutlets = useCallback(async (bounds) => {
    setOutletLoading(true);
    try {
      const params = { south: bounds.south, west: bounds.west, north: bounds.north, east: bounds.east, limit: 500 };
      if (zone) params.zone = zone;
      if (subzone) params.subzone = subzone;
      if (state) params.state = state;
      if (city) params.city = city;
      const res = await api.get('/map/outlets', { params });
      if (res.data.type === 'outlets') {
        setOutletData(res.data.data || []);
      }
      if (res.data.meta) setMeta(res.data.meta);
    } catch {} finally {
      setOutletLoading(false);
    }
  }, [api, zone, subzone, state, city]);

  const center = cityData.length > 0
    ? [cityData.reduce((s, d) => s + (d.avg_lat || 0), 0) / cityData.length,
       cityData.reduce((s, d) => s + (d.avg_lng || 0), 0) / cityData.length]
    : [20.5937, 78.9629];

  const maxCount = Math.max(...cityData.map(d => d.count || 1), 1);
  const getRadius = (count) => Math.max(4, Math.min(30, ((count || 1) / maxCount) * 30));

  const showOutlets = zoom >= 12;

  return (
    <div className="space-y-5" data-testid="map-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold tracking-tight">Map View</h1>
          <p className="text-sm text-muted-foreground mt-1">Geographic visualization of outlet distribution</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-3 items-center relative z-50">
        <Select value={zone || 'all'} onValueChange={v => setZone(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-52" data-testid="map-filter-zone"><SelectValue placeholder="All Zones" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Zones</SelectItem>
            {zones.map(z => (
              <SelectItem key={z.zone} value={z.zone}>
                {z.zone}
                <span className="text-[10px] text-muted-foreground ml-1.5">
                  ({z.mapped ? `${z.mapped.toLocaleString('en-IN')} mapped` : 'none mapped'})
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <GeoFilters api={api} zone={zone} subzone={subzone} setSubzone={setSubzone}
          state={state} setState={setState} city={city} setCity={setCity} />
        <div className="flex items-center gap-4 text-xs text-muted-foreground ml-4">
          {showOutlets ? (
            <>
              <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-emerald-500 inline-block" /> Growing/Stable</span>
              <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-amber-500 inline-block" /> Declining</span>
              <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-red-500 inline-block" /> Declining {'>'}25%</span>
              <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-gray-400 inline-block" /> No data</span>
            </>
          ) : (
            <>
              <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-emerald-500 inline-block" /> Active {'>'}80%</span>
              <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-amber-500 inline-block" /> Active 50-80%</span>
              <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-red-500 inline-block" /> Active {'<'}50%</span>
            </>
          )}
          {outletLoading && <Loader2 size={14} className="animate-spin text-primary" />}
        </div>
        {showOutlets && <span className="text-xs text-primary font-medium ml-auto">{outletData.length} outlets in view</span>}
      </div>

      <div className="bg-card border border-border rounded-md overflow-hidden" style={{ height: '560px', position: 'relative', zIndex: 0 }}>
        {/* Nothing to draw is usually missing coordinates, not a broken map — say so */}
        {!loading && ((showOutlets && !outletLoading && outletData.length === 0) || (!showOutlets && cityData.length === 0)) && (
          <div className="absolute inset-0 z-[500] flex items-center justify-center pointer-events-none">
            <div className="bg-card/95 border border-border rounded-md px-5 py-4 max-w-sm text-center shadow-lg pointer-events-auto">
              <p className="text-sm font-heading font-semibold">No outlets to plot here</p>
              {showOutlets ? (
                <p className="text-xs text-muted-foreground mt-1.5">
                  No outlets with GPS coordinates fall inside this view. Zoom out or pan to a marked area.
                </p>
              ) : meta && meta.mapped_outlets === 0 ? (
                <p className="text-xs text-muted-foreground mt-1.5">
                  {zone ? <>None of the {meta.total_outlets.toLocaleString('en-IN')} outlets in <strong>{zone}</strong> have GPS coordinates in the outlet master.</>
                        : <>No outlets have GPS coordinates in the outlet master.</>}
                  {' '}Upload an outlet master export that includes latitude and longitude.
                </p>
              ) : (
                <p className="text-xs text-muted-foreground mt-1.5">Try a different zone.</p>
              )}
            </div>
          </div>
        )}
        {loading ? (
          <div className="flex items-center justify-center h-full text-muted-foreground">Loading map data...</div>
        ) : (
          <MapContainer center={center} zoom={zone ? 8 : 5} style={{ height: '100%', width: '100%' }} scrollWheelZoom={true} key={`${zone}-${center[0]}`}>
            <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            <MapEventHandler onBoundsChange={loadOutlets} onZoomChange={setZoom} />

            {/* City-level circles (zoomed out) */}
            {!showOutlets && cityData.map((item, idx) => {
              if (!item.avg_lat || !item.avg_lng) return null;
              return (
                <CircleMarker key={`city-${idx}`} center={[item.avg_lat, item.avg_lng]} radius={getRadius(item.count)}
                  fillColor={getOutletColor(item)} color={getOutletColor(item)} weight={1} opacity={0.8} fillOpacity={0.5}>
                  <Popup>
                    <div className="text-xs space-y-1 font-body min-w-[140px]">
                      <p className="font-heading font-semibold text-sm">{item.city}</p>
                      <p>Zone: {item.zone}</p>
                      <p>Outlets: {item.count?.toLocaleString('en-IN')}</p>
                      <p>Active: {item.active?.toLocaleString('en-IN')}</p>
                    </div>
                  </Popup>
                </CircleMarker>
              );
            })}

            {/* Outlet-level markers (zoomed in) */}
            {showOutlets && (
              <MarkerClusterGroup chunkedLoading maxClusterRadius={50}
                iconCreateFunction={(cluster) => {
                  const count = cluster.getChildCount();
                  const size = count > 100 ? 'large' : count > 30 ? 'medium' : 'small';
                  const sizes = { small: 30, medium: 40, large: 50 };
                  return window.L.divIcon({
                    html: `<div style="background:rgba(27,58,140,0.85);color:white;border-radius:50%;width:${sizes[size]}px;height:${sizes[size]}px;display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:700;border:2px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.3);">${count}</div>`,
                    className: '', iconSize: [sizes[size], sizes[size]],
                  });
                }}>
                {outletData.map((item, idx) => {
                  if (!item.lat || !item.lng) return null;
                  const color = getOutletColor(item);
                  return (
                    <CircleMarker key={`outlet-${item.outlet_id || idx}`} center={[item.lat, item.lng]} radius={6}
                      fillColor={color} color={color} weight={2} opacity={1} fillOpacity={0.8}>
                      <Popup>
                        <div className="text-xs space-y-1.5 font-body min-w-[200px] max-w-[260px]">
                          <p className="font-heading font-semibold text-sm text-[#1B3A8C]">{item.name}</p>
                          <div className="flex gap-3 text-[11px] text-gray-500">
                            <span>Class: {item.classification || '-'}</span>
                            <span>Status: {item.status}</span>
                          </div>
                          <div className="border-t border-gray-200 pt-1.5 space-y-0.5">
                            <p><strong>Beat:</strong> {item.beat_name || '-'}</p>
                            <p><strong>Distributor:</strong> {item.distributor || '-'}</p>
                            <p><strong>Zone:</strong> {item.zone} | <strong>City:</strong> {item.city}</p>
                          </div>
                          {item.total_orders > 0 ? (
                            <div className="border-t border-gray-200 pt-1.5 space-y-0.5">
                              <p><strong>Total Orders:</strong> INR {formatINR(item.total_orders)}</p>
                              <p><strong>Fulfilled:</strong> INR {formatINR(item.total_fulfilled)} ({item.fulfilment_rate}%)</p>
                              <p><strong>SKUs Ordered:</strong> {item.sku_count}</p>
                              <p><strong>Order Count:</strong> {item.order_count?.toLocaleString('en-IN')}</p>
                              <div className="flex items-center gap-1 pt-0.5">
                                <strong>Trend:</strong>
                                {item.trend_pct > 0 ? (
                                  <span className="text-green-600 font-semibold flex items-center gap-0.5"><TrendingUp size={11} /> +{item.trend_pct}%</span>
                                ) : item.trend_pct < -5 ? (
                                  <span className="text-red-600 font-semibold flex items-center gap-0.5"><TrendingDown size={11} /> {item.trend_pct}%</span>
                                ) : (
                                  <span className="text-gray-500 flex items-center gap-0.5"><Minus size={11} /> Stable</span>
                                )}
                              </div>
                            </div>
                          ) : (
                            <div className="border-t border-gray-200 pt-1.5">
                              <p className="text-gray-400 italic">No order data available</p>
                            </div>
                          )}
                        </div>
                      </Popup>
                    </CircleMarker>
                  );
                })}
              </MarkerClusterGroup>
            )}
          </MapContainer>
        )}
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="bg-card border border-border rounded-md p-4">
          <p className="kpi-label text-muted-foreground">{showOutlets ? 'Outlets in View' : 'Locations'}</p>
          <p className="text-xl font-heading font-bold mt-1">{showOutlets ? outletData.length : cityData.length}</p>
        </div>
        <div className="bg-card border border-border rounded-md p-4">
          <p className="kpi-label text-muted-foreground">Total Outlets</p>
          <p className="text-xl font-heading font-bold mt-1">{showOutlets ? outletData.length : cityData.reduce((s, d) => s + (d.count || 1), 0).toLocaleString('en-IN')}</p>
        </div>
        <div className="bg-card border border-border rounded-md p-4">
          <p className="kpi-label text-muted-foreground">{showOutlets ? 'With Orders' : 'Active Outlets'}</p>
          <p className="text-xl font-heading font-bold mt-1">{showOutlets ? outletData.filter(o => o.total_orders > 0).length : cityData.reduce((s, d) => s + (d.active || 0), 0).toLocaleString('en-IN')}</p>
        </div>
        <div className="bg-card border border-border rounded-md p-4">
          <p className="kpi-label text-muted-foreground">Zoom Level</p>
          <p className="text-xl font-heading font-bold mt-1">{zoom} {zoom >= 12 ? '(Street)' : zoom >= 8 ? '(District)' : '(Overview)'}</p>
        </div>
      </div>

      {meta && meta.missing_coords > 0 && (
        <p className="text-xs text-amber-600 dark:text-amber-400 text-center" data-testid="map-coverage-note">
          Showing {meta.mapped_outlets.toLocaleString('en-IN')} of {meta.total_outlets.toLocaleString('en-IN')} outlets
          {zone ? ` in ${zone}` : ''} ({meta.coverage_pct}%) — the other {meta.missing_coords.toLocaleString('en-IN')} have
          no latitude/longitude in the outlet master and cannot be plotted.
        </p>
      )}
      {!showOutlets && (
        <p className="text-xs text-muted-foreground text-center">Zoom in to street level (zoom 12+) to see individual outlets with sales data</p>
      )}
    </div>
  );
}
